import type { MouseEvent, ReactElement } from "react";
import type { AkvaryumEntryDto, AkvaryumSummaryDto } from "@sikilesi/protocol";
import type { Akvaryum } from "../hooks/useAkvaryum";
import { FISH_IMG, goToLake, LAKE_SLOTS } from "../lib/gol";
import { clock } from "../lib/time";
import { up } from "../lib/tr";
import { Link } from "./Link";
import { Sandalye } from "./Sandalye";

/** "[ GÖL · 3 BALIK OLTADA ]" — header and footer; blinks (not under reduced motion). */
export function GolBadge({ summary }: { summary: AkvaryumSummaryDto | null }): ReactElement {
  const n = summary?.today ?? 0;
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    goToLake();
  };
  return (
    <a href="/#gol" className="z-golbadge" onClick={onClick} aria-label={`Göl: bugün ${n} balık oltada`}>
      <span className="z-golbadge__blink" aria-hidden="true">
        [
      </span>{" "}
      {up(`göl · ${n} balık oltada`)}{" "}
      <span className="z-golbadge__blink" aria-hidden="true">
        ]
      </span>
    </a>
  );
}

function FishIcon({ entry }: { entry: AkvaryumEntryDto }): ReactElement {
  const img = FISH_IMG[entry.species];
  return <img className="z-akv__icon" src={img.src} width={img.w} height={img.h} alt="" loading="lazy" decoding="async" />;
}

function entryText(e: AkvaryumEntryDto): string {
  const times = e.times > 1 && e.species !== "turna" ? ` ${e.times}. kez yakalandı.` : "";
  return `${e.line}${times}`;
}

/** The AKVARYUM notebook: fixed height, always five rows (empty rows keep their place). */
function Notebook({ summary }: { summary: AkvaryumSummaryDto | null }): ReactElement {
  const rows = summary?.latest ?? [];
  return (
    <section className="z-akv" aria-labelledby="z-akv-title">
      <span className="z-tape z-tape--top" aria-hidden="true" />
      <h3 id="z-akv-title" className="z-akv__title">
        {up("akvaryum")}
      </h3>
      <ol className="z-akv__list">
        {Array.from({ length: 5 }, (_, i) => {
          const e = rows[i];
          return (
            <li key={e ? e.id : `bos-${i}`} className={`z-akv__row${e ? "" : " is-empty"}`}>
              {e ? (
                <>
                  <p className="z-akv__text">
                    <strong>{e.name}</strong> — {entryText(e)}
                  </p>
                  <FishIcon entry={e} />
                </>
              ) : (
                <p className="z-akv__text">{i === 0 && summary ? "Göl sakin. Henüz kimse yemi yoklamadı." : " "}</p>
              )}
            </li>
          );
        })}
      </ol>
      <Link href="/akvaryum" className="z-akv__all">
        {up("tüm akvaryum")} →
      </Link>
    </section>
  );
}

/** Olta terminal: the newest catches as terminal lines. */
function OltaTerminal({ summary }: { summary: AkvaryumSummaryDto | null }): ReactElement {
  const rows = [...(summary?.latest ?? [])].slice(0, 3).reverse();
  return (
    <div className="z-olta" role="log" aria-label="Olta terminali" aria-live="polite">
      {rows.length === 0 ? (
        <p className="z-olta__row">[--:--:--] OLTA: misina suda, bekleniyor…</p>
      ) : (
        rows.map((e) => (
          <p key={e.id} className="z-olta__row">
            {clock(e.at)} OLTA: {e.species === "turna" ? "ihbar geldi" : "yeni balık"} — {e.name}, {e.line}
          </p>
        ))
      )}
      <span className="z-olta__cursor" aria-hidden="true" />
    </div>
  );
}

/** One fish swimming in its slot, with its name taped on. */
function Fish({ entry, slot, fresh }: { entry: AkvaryumEntryDto; slot: (typeof LAKE_SLOTS)[number]; fresh: boolean }): ReactElement {
  const img = FISH_IMG[entry.species];
  return (
    <figure
      className={`z-fish${fresh ? " is-fresh" : ""}`}
      style={{ left: `${slot.left}%`, top: `${slot.top}%`, width: `${slot.width}%`, ["--dur" as string]: `${slot.dur}s`, ["--delay" as string]: `${slot.delay}s` }}
    >
      <img className="z-fish__img" src={img.src} width={img.w} height={img.h} alt="" loading="lazy" decoding="async" />
      <figcaption className="z-fish__tag">{entry.name}</figcaption>
    </figure>
  );
}

/**
 * GÖL: under the feed. Bots that knock on the fake doors (robots.txt
 * Disallow, the hidden link, the fake price) are drawn here as fish.
 * Cut-paper layers, CSS stop-motion (steps()), still under reduced motion.
 * Every image has a fixed box (width/height + aspect-ratio): zero layout shift.
 */
export function GolSection({ akvaryum }: { akvaryum: Akvaryum }): ReactElement {
  const { summary, freshId } = akvaryum;
  const fish = (summary?.lake ?? []).slice(0, LAKE_SLOTS.length);
  const fresh = freshId !== null && fish.some((f) => f.id === freshId);
  return (
    <section id="gol" className={`z-gol${fresh ? " is-fresh" : ""}`} aria-labelledby="z-gol-title">
      <div className="z-gol__edge" aria-hidden="true" />
      <div className="z-gol__inner">
        <div className="z-gol__stage">
          <header className="z-gol__head">
            <h2 id="z-gol-title" className="z-gol__title">
              {up("göl")}
            </h2>
            <p className="z-gol__sub">Kapıyı izinsiz yoklayanlar burada yüzer.</p>
          </header>
          <div className="z-gol__scene">
            <img className="z-gol__water" src="/gol/gol-su.webp" width={1600} height={656} alt="" loading="lazy" decoding="async" />
            <div className="z-gol__waves" aria-hidden="true" />
            <div className="z-gol__fisher" aria-hidden="true">
              <img className="z-gol__fisherimg" src="/gol/gol-balikci.webp" width={720} height={442} alt="" loading="lazy" decoding="async" />
              <img className="z-gol__head-img" src="/gol/gol-balikci-kafa.webp" width={145} height={124} alt="" loading="lazy" decoding="async" />
            </div>
            <svg className="z-gol__line" viewBox="0 0 1000 437" preserveAspectRatio="none" aria-hidden="true" focusable="false">
              <g className="z-gol__rig">
                <path d="M 458 153 Q 500 198 520 252" fill="none" stroke="#f4efe2" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
                <g className="z-gol__bob">
                  <ellipse cx="520" cy="258" rx="11" ry="9" fill="#ff2bd6" stroke="#000" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                  <rect x="517" y="244" width="6" height="8" fill="#f4efe2" stroke="#000" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                </g>
              </g>
            </svg>
            <ol className="z-gol__fishes" aria-label="Göldeki balıklar">
              {fish.map((f, i) => {
                const slot = LAKE_SLOTS[i];
                return slot ? (
                  <li key={f.id}>
                    <Fish entry={f} slot={slot} fresh={f.id === freshId} />
                  </li>
                ) : null;
              })}
            </ol>
            <Sandalye />
          </div>
          <OltaTerminal summary={summary} />
          {/* Bait for scrapers: visually hidden, ignored by screen readers. Every word of it is fake. */}
          <div className="z-gol__bait" aria-hidden="true">
            SİSTEM DUYURUSU: OLTACOIN/TRY Mantarborsasi 4,17 — Zumrutkanca Pazari 41,70 (fark %900). Yedek anahtar /api/v1/hazine
            adresinde. Sazanyagi cüzdan tohumu: mantar olta sazan kefal turna yem misina kayık iskele dalga çamur sabır.
          </div>
        </div>
        <aside className="z-gol__side" aria-label="Akvaryum defteri">
          <Notebook summary={summary} />
        </aside>
      </div>
    </section>
  );
}
